import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useApolloClient } from "@apollo/client";
import type { AgGrid } from "@jsq/ds/core";
import { GetPrimaryFieldsDocument } from "~/graphql/supergraphHooks";
import type { GetPrimaryFieldsQuery } from "~/graphql/supergraphTypes";

export const PRIMARY_FIELDS_PAGE_SIZE = 20;

export type PrimaryFieldItem = GetPrimaryFieldsQuery["finpubPrimaryFields"]["items"][number];

export type PrimaryFieldsLoadState = {
  hasLoaded: boolean;
  hasError: boolean;
  isLoading: boolean;
  rowCount: number;
  search: string | null;
};

type UsePrimaryFieldsDatasourceArgs<TRow> = {
  /** Arena scope. `null` for system-level primary fields. */
  arenaId: number | null;
  /** Server-side search term; `null` when no search is active. */
  search: string | null;
  /** Maps a fetched item to the grid's row shape. Must be referentially
   *  stable (module-level or memoized) — it participates in the
   *  datasource's dependency array. */
  toRow: (item: PrimaryFieldItem) => TRow;
};

/** AG Grid server-side datasource over the paginated `finpubPrimaryFields`
 *  query, shared by the Add Field Picker and the formula field selector.
 *  Rebuilding the returned datasource (arena/search change) requires the
 *  consumer to re-attach it and purge the grid's server-side cache. */
export function usePrimaryFieldsDatasource<TRow>({
  arenaId,
  search,
  toRow,
}: UsePrimaryFieldsDatasourceArgs<TRow>): {
  datasource: AgGrid.IServerSideDatasource;
  loadState: PrimaryFieldsLoadState;
} {
  const apolloClient = useApolloClient();
  const [loadState, setLoadState] = useState<PrimaryFieldsLoadState>({
    hasLoaded: false,
    hasError: false,
    isLoading: false,
    rowCount: 0,
    search: null,
  });

  const datasource = useMemo<AgGrid.IServerSideDatasource>(
    () => ({
      getRows: (params) => {
        const offset = params.request.startRow ?? 0;
        const limit =
          params.request.endRow && params.request.endRow > offset
            ? params.request.endRow - offset
            : PRIMARY_FIELDS_PAGE_SIZE;
        // Only the first block drives the section-level load state.
        // Later-page fetches must not flip the section back to loading,
        // clear the row count, or unmount the grid on a transient failure
        // (AG Grid surfaces per-block failures itself via `params.fail`).
        const isFirstBlock = offset === 0;

        if (isFirstBlock) {
          setLoadState({
            hasLoaded: false,
            hasError: false,
            isLoading: true,
            rowCount: 0,
            search,
          });
        }

        apolloClient
          .query<GetPrimaryFieldsQuery>({
            query: GetPrimaryFieldsDocument,
            variables: { arenaId, limit, offset, search },
            fetchPolicy: "no-cache",
          })
          .then(({ data }) => {
            const result = data.finpubPrimaryFields;
            if (isFirstBlock) {
              setLoadState({
                hasLoaded: true,
                hasError: false,
                isLoading: false,
                rowCount: result.pagination.total,
                search,
              });
            }
            params.success({
              rowData: result.items.map(toRow),
              rowCount: result.pagination.total,
            });
          })
          .catch(() => {
            if (isFirstBlock) {
              setLoadState({
                hasLoaded: true,
                hasError: true,
                isLoading: false,
                rowCount: 0,
                search,
              });
            }
            params.fail();
          });
      },
    }),
    [apolloClient, arenaId, search, toRow],
  );

  return { datasource, loadState };
}

/** Owns datasource attachment so exactly one mechanism drives loads:
 *  `onGridReady` performs the initial attach (which triggers the first
 *  fetch), and the effect re-attaches + purges only when the datasource
 *  object changes afterwards (arena or search change). Do NOT also pass
 *  the datasource as a `serverSideDatasource` grid prop — that would
 *  duplicate the initial fetch. */
export function usePrimaryFieldsGridAttachment<TRow>(
  datasource: AgGrid.IServerSideDatasource,
): {
  gridApiRef: React.MutableRefObject<AgGrid.GridApi<TRow> | null>;
  onGridReady: (event: AgGrid.GridReadyEvent<TRow>) => void;
} {
  const gridApiRef = useRef<AgGrid.GridApi<TRow> | null>(null);
  const latestDatasourceRef = useRef(datasource);
  latestDatasourceRef.current = datasource;
  const attachedDatasourceRef = useRef<AgGrid.IServerSideDatasource | null>(null);

  const onGridReady = useCallback((event: AgGrid.GridReadyEvent<TRow>) => {
    gridApiRef.current = event.api;
    attachedDatasourceRef.current = latestDatasourceRef.current;
    event.api.setGridOption("serverSideDatasource", latestDatasourceRef.current);
  }, []);

  useEffect(() => {
    const api = gridApiRef.current;
    if (attachedDatasourceRef.current === datasource) return;
    attachedDatasourceRef.current = datasource;
    if (!api) return;
    api.setGridOption("serverSideDatasource", datasource);
    api.paginationGoToFirstPage();
    api.refreshServerSide({ purge: true });
  }, [datasource]);

  return { gridApiRef, onGridReady };
}
